import type { IndicatorDelta } from '@ederest/contracts';

/** Wording of the compared indicators; the API only names their key. */
export const INDICATOR_LABELS: Record<IndicatorDelta['key'], string> = {
  completion_rate: 'Usagers qui vont au bout',
  drop_off_rate: "Taux d'abandon",
  abandon_rate: 'Abandon sur le champ',
  error_rate: "Taux d'erreur",
  reentry_rate: 'Reprises de saisie',
  friction_score: 'Score de friction',
  median_duration_ms: 'Temps médian',
  hesitations_per_session: 'Hésitations par session',
  errors_per_session: 'Erreurs par session',
  rage_clicks_per_session: 'Clics répétés par session',
};

/** Short column names of the step table, then what each one measures, in plain words. */
export const COLUMN_LABELS: Partial<Record<IndicatorDelta['key'], string>> = {
  drop_off_rate: 'Abandon',
  friction_score: 'Friction',
  median_duration_ms: 'Temps passé',
  hesitations_per_session: 'Hésitations',
  errors_per_session: 'Erreurs',
};

export const COLUMN_HINTS: Partial<Record<IndicatorDelta['key'], string>> = {
  drop_off_rate:
    'Part des usagers qui quittent le site à cette étape. Sur une ligne de champ : part de ceux qui partent depuis ce champ.',
  friction_score:
    'Difficulté mesurée de 0 à 100 : hésitations, erreurs, clics répétés et abandons. Elle doit baisser.',
  median_duration_ms: "Temps qu'un usager sur deux passe, au plus, sur l'étape ou le champ.",
  hesitations_per_session: 'Arrêts prolongés sur un champ sans rien saisir, en moyenne par usager.',
  errors_per_session:
    "Messages d'erreur vus, en moyenne par usager. Sur une ligne de champ : part des usagers qui y ont vu une erreur.",
};

export const DIRECTION_LABELS: Record<IndicatorDelta['direction'], string> = {
  improved: 'Amélioration',
  worsened: 'Dégradation',
  unchanged: 'Stable',
};

const fr = (value: string) => value.replace('.', ',');

/** Renders a value in the unit of its indicator, in French. */
export function formatIndicatorValue(value: number, unit: IndicatorDelta['unit']): string {
  switch (unit) {
    case 'rate':
      return `${fr((value * 100).toFixed(1))} %`;
    case 'score':
      return Math.round(value).toString();
    case 'ms':
      return value === 0 ? '—' : `${fr((value / 1000).toFixed(value >= 10_000 ? 0 : 1))} s`;
    case 'per_session':
      return fr(value.toFixed(2));
  }
}

/** The movement of an indicator, signed, in its unit: points for rates and scores, seconds for durations. */
export function formatIndicatorDelta(delta: IndicatorDelta): string {
  const sign = delta.absoluteDelta > 0 ? '+' : delta.absoluteDelta < 0 ? '−' : '';
  const magnitude = Math.abs(delta.absoluteDelta);
  switch (delta.unit) {
    case 'rate':
      return `${sign}${fr((magnitude * 100).toFixed(1))} pt`;
    case 'score':
      return `${sign}${Math.round(magnitude)} pt`;
    case 'ms':
      return `${sign}${fr((magnitude / 1000).toFixed(1))} s`;
    case 'per_session':
      return `${sign}${fr(magnitude.toFixed(2))}`;
  }
}

export function formatDay(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

export const COMPONENT_LABELS: Record<string, string> = {
  help_bubble: "Bulle d'aide",
  guided_tour: 'Parcours guidé',
  checklist: 'Liste de contrôle',
  assisted_field: 'Champ assisté',
  progress_indicator: 'Indicateur de progression',
  warning: 'Avertissement',
};

/** The effect in one sentence: what was published, when, and what moved since. */
export function effectSentence(summary: {
  releaseVersion: number;
  publishedAt: string;
  days: number;
  guidances: ReadonlyArray<{ component: string; stepName: string | null }>;
  lowConfidence: boolean;
  completionRate: IndicatorDelta;
}): string {
  const published = new Date(summary.publishedAt).toLocaleDateString('fr-FR');
  const [first] = summary.guidances;
  // Named in parentheses so the sentence holds whatever the component's gender ("bulle", "champ"…).
  const what = first
    ? ` (${COMPONENT_LABELS[first.component] ?? first.component}${first.stepName ? ` sur « ${first.stepName} »` : ''})`
    : '';
  const completion = summary.completionRate;
  if (!isEffectMeasurable(summary)) {
    return summary.guidances.length === 0
      ? `La version ${summary.releaseVersion} n'a ajouté aucune aide : il n'y a pas d'effet à mesurer.`
      : `Effet pas encore mesurable : trop peu d'usagers mesurés avant ou après la publication du ${published}.`;
  }
  const before = formatIndicatorValue(completion.beforeValue, completion.unit);
  const after = formatIndicatorValue(completion.afterValue, completion.unit);
  if (completion.direction === 'unchanged') {
    return `Depuis la publication du ${published}${what}, la part des usagers qui vont au bout de leur démarche reste stable (${after}).`;
  }
  const verb = completion.direction === 'improved' ? 'est passée' : 'est retombée';
  return `Depuis la publication du ${published}${what}, la part des usagers qui vont au bout de leur démarche ${verb} de ${before} à ${after}.`;
}

/**
 * Whether a publication's effect may be stated: the version added at least one
 * guidance and both windows count enough sessions. Otherwise the Console says
 * the effect is not measurable yet rather than print a figure nobody should read.
 */
export function isEffectMeasurable(summary: {
  guidances: readonly unknown[];
  lowConfidence: boolean;
  completionRate: IndicatorDelta;
}): boolean {
  return summary.guidances.length > 0 && !summary.lowConfidence && !summary.completionRate.lowConfidence;
}
